import React, { useRef } from "react";
import { Divider, Menu, Skeleton } from "antd";
import { Link } from "react-router-dom";
import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import IntlMessages from "../../util/IntlMessages";
import { layoutSettingsVar } from "../../apollo/local/cache";
import { useQuery, useReactiveVar } from "@apollo/client";
import { startsWith } from "lodash";
import { injectIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAdminPath } from "../../util/router";
import {
   faUsersLine,
   faGauge,
   faRecycle,
   faBell,
   faNewspaper,
   faBuilding,
   faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import { GQL_QUERY_ME } from "../../apollo/query/user";
import { ACLSubject } from "../../constants/Enums";
import { isAdmin } from "../../acl";

function permissionsHaveSubject(permissions, subject) {
   for (let i = 0; i < permissions.length; i++) {
      if (permissions[i].subject === subject) {
         return true;
      }
   }
   return false;
}

const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed, intl, user }) => {
   //assicura first load senza ricaricare la sidebar
   const sidebarLoaded = useRef(null);

   const { loading } = useQuery(GQL_QUERY_ME, {
      onCompleted() {
         if (!sidebarLoaded.current) {
            sidebarLoaded.current = true;
         }
      },
   });

   let { themeType } = useReactiveVar(layoutSettingsVar);

   //Selected menu item highlight
   const pathname = window.location.pathname;
   let selectedKey = "";

   const menuKeys = [
      getAdminPath("dashboard"),
      getAdminPath("reports"),
      getAdminPath("tax-providers"),
      getAdminPath("public-entities"),
      getAdminPath("users"),
      getAdminPath("news"),
      getAdminPath("events"),
      getAdminPath("logs"),
      getAdminPath("settings"),
   ];

   menuKeys.forEach((key) => {
      if (pathname === key || startsWith(pathname, key + "/")) {
         selectedKey = key;
         return;
      }
   });
   //

   if (!sidebarLoaded.current && loading) {
      return <Skeleton active={true} />;
   }

   const permissions = user?.acl_permissions || [];

   return (
      <>
         <SidebarLogo
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
         />

         <div
            className="gx-sidebar-content"
            style={{ height: "calc(100vh - 72px)" }}
         >
            <CustomScrollbars className="gx-layout-sider-scrollbar_todo">
               <Menu
                  selectedKeys={[selectedKey]}
                  theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                  mode="inline"
                  style={{ overflowX: "hidden" }}
               >
                  <Menu.ItemGroup title={null}>
                     {isAdmin(user) && (
                        <Menu.Item key={getAdminPath("dashboard")}>
                           <Link to={getAdminPath("dashboard")}>
                              <span className="icon">
                                 <FontAwesomeIcon icon={faGauge} />
                              </span>
                              <span>
                                 <IntlMessages id="sidebar.dashboard" />
                              </span>
                           </Link>
                        </Menu.Item>
                     )}

                     {permissionsHaveSubject(permissions, ACLSubject.REPORT) && (
                        <Menu.Item key={getAdminPath("reports")}>
                           <Link to={getAdminPath("reports")}>
                              <span className="icon">
                                 <FontAwesomeIcon icon={faBell} />
                              </span>
                              <span>
                                 <IntlMessages id="sidebar.reports" />
                              </span>
                           </Link>
                        </Menu.Item>
                     )}
                  </Menu.ItemGroup>

                  {isAdmin(user) && (
                     <Menu.ItemGroup
                        title={intl
                           .formatMessage({ id: "sidebar.admin" })
                           .toUpperCase()}
                     >
                        {permissionsHaveSubject(
                           permissions,
                           ACLSubject.TAX_PROVIDER
                        ) && (
                           <Menu.Item key={getAdminPath("tax-providers")}>
                              <Link to={getAdminPath("tax-providers")}>
                                 <span className="icon">
                                    <FontAwesomeIcon icon={faRecycle} />
                                 </span>
                                 <span>
                                    <IntlMessages id="sidebar.tax_providers" />
                                 </span>
                              </Link>
                           </Menu.Item>
                        )}

                        {permissionsHaveSubject(
                           permissions,
                           ACLSubject.PUBLIC_ENTITY
                        ) && (
                           <Menu.Item key={getAdminPath("public-entities")}>
                              <Link to={getAdminPath("public-entities")}>
                                 <span className="icon">
                                    <FontAwesomeIcon icon={faBuilding} />
                                 </span>
                                 <span>
                                    <IntlMessages id="sidebar.public_entities" />
                                 </span>
                              </Link>
                           </Menu.Item>
                        )}

                        {permissionsHaveSubject(
                           permissions,
                           ACLSubject.NEWS
                        ) && (
                           <Menu.Item key={getAdminPath("news")}>
                              <Link to={getAdminPath("news")}>
                                 <span className="icon">
                                    <FontAwesomeIcon icon={faNewspaper} />
                                 </span>
                                 <span>
                                    <IntlMessages id="sidebar.news" />
                                 </span>
                              </Link>
                           </Menu.Item>
                        )}

                        {permissionsHaveSubject(
                           permissions,
                           ACLSubject.EVENT
                        ) && (
                           <Menu.Item key={getAdminPath("events")}>
                              <Link to={getAdminPath("events")}>
                                 <span className="icon">
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                 </span>
                                 <span>
                                    <IntlMessages id="sidebar.events" />
                                 </span>
                              </Link>
                           </Menu.Item>
                        )}

                        <Divider key="divider-sidebar-menu" />

                        {permissionsHaveSubject(permissions, ACLSubject.USER) && (
                           <>
                              <Menu.Item key={getAdminPath("users")}>
                                 <Link to={getAdminPath("users")}>
                                    <span className="icon">
                                       <FontAwesomeIcon icon={faUsersLine} />
                                    </span>
                                    <span>
                                       <IntlMessages id="sidebar.users" />
                                    </span>
                                 </Link>
                              </Menu.Item>

                              {/* <Menu.Item key={getAdminPath("settings")}>
                           <Link to={getAdminPath("settings")}>
                              <span className="icon">
                                 <FontAwesomeIcon icon={faGears} />
                              </span>
                              <span>
                                 <IntlMessages id="sidebar.settings" />
                              </span>
                           </Link>
                        </Menu.Item> */}

                              {/* <Menu.Item key={getAdminPath("logs")}>
                                 <Link to={getAdminPath("logs")}>
                                    <span className="icon">
                                       <FontAwesomeIcon icon={faXmarksLines} />
                                    </span>
                                    <span>
                                       <IntlMessages id="common.logs" />
                                    </span>
                                 </Link>
                              </Menu.Item> */}
                           </>
                        )}
                     </Menu.ItemGroup>
                  )}
               </Menu>
            </CustomScrollbars>
         </div>
      </>
   );
};

SidebarContent.propTypes = {};

export default injectIntl(SidebarContent);
